import type { User } from '@coachapp/shared';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios, { type AxiosError } from 'axios';
import client from '../api/client';
import { clearLocalData, ensureCacheOwner, setCacheOwnerUserId } from '../offline';
import { coachApi } from './coachApi';
import type { RootState } from './index';
import { resetSync } from './syncSlice';

interface AuthState {
  user: User | null;
  isLoading: boolean;
  /** Bumps on session reset — remounts authenticated UI for a clean slate. */
  sessionKey: number;
}

function hasStoredToken(): boolean {
  return typeof localStorage !== 'undefined' && Boolean(localStorage.getItem('token'));
}

const initialState: AuthState = {
  user: hasStoredToken() ? readCachedUser() : null,
  isLoading: hasStoredToken(),
  sessionKey: 0,
};

function isAuthError(error: AxiosError): boolean {
  const status = error.response?.status;
  return status === 401 || status === 403;
}

function clearAuthStorage(): void {
  localStorage.removeItem('user');
  localStorage.removeItem('token');
}

function readCachedUser(): User | null {
  const storedUser = localStorage.getItem('user');
  if (!storedUser) {
    return null;
  }
  try {
    return JSON.parse(storedUser) as User;
  } catch {
    localStorage.removeItem('user');
    return null;
  }
}

async function clearAuthSession(dispatch: (action: unknown) => void): Promise<void> {
  clearAuthStorage();
  await clearLocalData();
  dispatch(coachApi.util.resetApiState());
  dispatch(resetSync());
}

export const initializeAuth = createAsyncThunk('auth/initialize', async (_, { dispatch }) => {
  const token = localStorage.getItem('token');
  if (!token) {
    await clearAuthSession(dispatch);
    return null;
  }

  try {
    const { data } = await client.get<User>('/auth/me');
    localStorage.setItem('user', JSON.stringify(data));

    const cacheKept = await ensureCacheOwner(data.id);
    if (!cacheKept) {
      dispatch(coachApi.util.resetApiState());
      dispatch(resetSync());
    }

    return data;
  } catch (err) {
    if (axios.isAxiosError(err) && isAuthError(err)) {
      await clearAuthSession(dispatch);
      return null;
    }

    const cachedUser = readCachedUser();
    if (cachedUser) {
      const cacheKept = await ensureCacheOwner(cachedUser.id);
      if (!cacheKept) {
        dispatch(coachApi.util.resetApiState());
        dispatch(resetSync());
      }
      return cachedUser;
    }

    await clearAuthSession(dispatch);
    return null;
  }
});

export const signIn = createAsyncThunk(
  'auth/signIn',
  async ({ email, password }: { email: string; password: string }, { dispatch }) => {
    clearAuthStorage();
    await clearLocalData();
    dispatch(coachApi.util.resetApiState());
    dispatch(resetSync());

    const response = await client.post('/auth/login', { email, password });
    const { user, access_token } = response.data as { user: User; access_token: string };

    localStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('token', access_token);
    await setCacheOwnerUserId(user.id);
    return user;
  },
);

export const signOut = createAsyncThunk('auth/signOut', async (_, { dispatch }) => {
  clearAuthStorage();
  await clearLocalData();
  dispatch(coachApi.util.resetApiState());
  dispatch(resetSync());
});

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser(state, action: { payload: User | null }) {
      state.user = action.payload;
    },
    setLoading(state, action: { payload: boolean }) {
      state.isLoading = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(initializeAuth.pending, (state) => {
        const token = localStorage.getItem('token');
        if (!token) {
          return;
        }
        state.isLoading = true;
        if (!state.user) {
          state.user = readCachedUser();
        }
      })
      .addCase(initializeAuth.fulfilled, (state, action) => {
        state.user = action.payload;
        state.isLoading = false;
      })
      .addCase(initializeAuth.rejected, (state) => {
        state.user = null;
        state.isLoading = false;
      })
      .addCase(signIn.pending, (state) => {
        state.user = null;
        state.sessionKey += 1;
      })
      .addCase(signIn.fulfilled, (state, action) => {
        state.user = action.payload;
      })
      .addCase(signOut.pending, (state) => {
        state.user = null;
        state.sessionKey += 1;
      })
      .addCase(signOut.fulfilled, (state) => {
        state.user = null;
      });
  },
});

export const { setUser, setLoading } = authSlice.actions;
export default authSlice.reducer;

export const selectUser = (state: RootState) => state.auth.user;
export const selectAuthLoading = (state: RootState) => state.auth.isLoading;
export const selectSessionKey = (state: RootState) => state.auth.sessionKey;
